import { useMutation } from '@apollo/client';
import { differenceByAllKeys } from '@openx/components-form/difference';
import { noop } from '@openx/utils/noop';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../../components/AuthContext/context';
import { CreateCuratedDealBuyerDiscountDocument } from '../../../../graphql/mutation/curatedDealBuyerDiscount/CreateCuratedDealBuyerDiscount.generated';
import { UpdateCuratedDealBuyerDiscountDocument } from '../../../../graphql/mutation/curatedDealBuyerDiscount/UpdateCuratedDealBuyerDiscount.generated';
import { GetCuratedDealBuyerDiscountDocument } from '../../../../graphql/query/curatedDealBuyerDiscount/GetCuratedDealBuyerDiscount.generated';
import { type NestedError, NestedErrorCode } from '../../../graphql/types';
import { useLocalErrorHandling } from '../../../graphql/useLocalErrorHandling';
import type { CuratedDealBuyerDiscountsFormData } from '../types';
import { createCuratedDealBuyerDiscountApiPayload } from '../utils/createCuratedDealBuyerDiscountApiPayload';

export const useCuratedDealBuyerDiscountFormSubmit = (
  defaultValues: CuratedDealBuyerDiscountsFormData,
  curatedDealBuyerDiscountUid?: string
) => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const accountUid = session?.exchange_session?.account?.uid;
  const isEdit = !!curatedDealBuyerDiscountUid;

  if (!accountUid) {
    throw new Error('session with account.uid is required');
  }

  const [createCuratedDealBuyerDiscount, { loading }] = useMutation(CreateCuratedDealBuyerDiscountDocument, {
    onCompleted: () => {
      enqueueSnackbar({ message: t('Discount successfully created.'), variant: 'success' });
      navigate('/discounts');
    },
  });

  const [updateCuratedDealBuyerDiscount] = useMutation(UpdateCuratedDealBuyerDiscountDocument, {
    onCompleted: () => {
      enqueueSnackbar({ message: t('Discount successfully updated.'), variant: 'success' });
      navigate('/discounts');
    },
    refetchQueries: [{ query: GetCuratedDealBuyerDiscountDocument, variables: { uid: curatedDealBuyerDiscountUid } }],
  });

  const errorHandler = useCallback(
    (error: NestedError): boolean => {
      if (error.code === NestedErrorCode.NOT_UNIQUE_ERROR && error.path === 'demand_partner_id') {
        enqueueSnackbar(t('Discount for selected DSP already exists.'), { variant: 'error' });
        return true;
      }

      if (error.code === NestedErrorCode.NOT_UNIQUE_ERROR && error.path === 'name') {
        enqueueSnackbar(t('Discount Name is not unique.'), { variant: 'error' });
        return true;
      }

      return false;
    },
    [enqueueSnackbar, t]
  );

  const submitHandler = useCallback(
    (formData: CuratedDealBuyerDiscountsFormData) => {
      const newCuratedDealBuyerDiscount = createCuratedDealBuyerDiscountApiPayload(accountUid, formData);

      if (!isEdit) {
        return createCuratedDealBuyerDiscount({
          variables: {
            payload: newCuratedDealBuyerDiscount,
          },
        }).catch(noop);
      }

      const oldCuratedDealBuyerDiscount = createCuratedDealBuyerDiscountApiPayload(accountUid, defaultValues);

      const diff = differenceByAllKeys(newCuratedDealBuyerDiscount, oldCuratedDealBuyerDiscount);

      return updateCuratedDealBuyerDiscount({
        variables: {
          payload: diff,
          uid: curatedDealBuyerDiscountUid,
        },
      }).catch(noop);
    },
    [
      accountUid,
      createCuratedDealBuyerDiscount,
      curatedDealBuyerDiscountUid,
      defaultValues,
      isEdit,
      updateCuratedDealBuyerDiscount,
    ]
  );

  useLocalErrorHandling(CreateCuratedDealBuyerDiscountDocument, errorHandler);

  return {
    loading,
    submitHandler,
  };
};
